import { http } from '@/lib/apiClient';
import type {
  CreateCommentoInput,
  CreateTicketInput,
  Paginated,
  SlaPolicy,
  TicketAllegato,
  TicketCommento,
  TicketConRelazioni,
  TicketFilters,
  UUID,
  UpdateTicketInput,
  Utente,
  Operatore,
} from '@/types';

const BASE = '/ticket';

export const ticketService = {
  list(filters: TicketFilters = {}): Promise<Paginated<TicketConRelazioni>> {
    return http.get<Paginated<TicketConRelazioni>>(BASE, { params: filters });
  },

  get(id: UUID): Promise<TicketConRelazioni> {
    return http.get<TicketConRelazioni>(`${BASE}/${id}`);
  },

  create(input: CreateTicketInput): Promise<TicketConRelazioni> {
    return http.post<TicketConRelazioni, CreateTicketInput>(BASE, input);
  },

  update(id: UUID, input: UpdateTicketInput): Promise<TicketConRelazioni> {
    return http.patch<TicketConRelazioni, UpdateTicketInput>(`${BASE}/${id}`, input);
  },

  /** Assignment is its own endpoint so the backend can notify the assignee. */
  assign(id: UUID, utenteId: UUID | null): Promise<TicketConRelazioni> {
    return http.post<TicketConRelazioni>(`${BASE}/${id}/assign`, { assegnato_a: utenteId });
  },

  listCommenti(id: UUID): Promise<TicketCommento[]> {
    return http.get<TicketCommento[]>(`${BASE}/${id}/commenti`);
  },

  addCommento(id: UUID, input: CreateCommentoInput): Promise<TicketCommento> {
    return http.post<TicketCommento, CreateCommentoInput>(`${BASE}/${id}/commenti`, input);
  },

  listAllegati(id: UUID): Promise<TicketAllegato[]> {
    return http.get<TicketAllegato[]>(`${BASE}/${id}/allegati`);
  },

  /**
   * Uploads one file. `Content-Type` is deleted so the browser sets the
   * multipart boundary itself, and `onProgress` feeds the upload progress bar.
   */
  uploadAllegato(id: UUID, file: File, onProgress?: (percent: number) => void): Promise<TicketAllegato> {
    const form = new FormData();
    form.append('file', file);

    return http.post<TicketAllegato, FormData>(`${BASE}/${id}/allegati`, form, {
      headers: { 'Content-Type': undefined },
      onUploadProgress: (event) => {
        if (!onProgress || !event.total) return;
        onProgress(Math.round((event.loaded / event.total) * 100));
      },
    });
  },

  deleteAllegato(ticketId: UUID, allegatoId: UUID): Promise<void> {
    return http.delete(`${BASE}/${ticketId}/allegati/${allegatoId}`);
  },

  /** Returns a short-lived signed URL rather than streaming through the app. */
  getAllegatoUrl(ticketId: UUID, allegatoId: UUID): Promise<{ url: string; expiresIn: number }> {
    return http.get<{ url: string; expiresIn: number }>(`${BASE}/${ticketId}/allegati/${allegatoId}/url`);
  },

  /** Operators available for assignment, optionally filtered by ticket category. */
  listAssignees(categoriaId?: UUID): Promise<Operatore[]> {
    return http.get<Operatore[]>('/operatori', {
      params: categoriaId ? { categoria_id: categoriaId } : undefined,
    });
  },

  listSlaPolicies(): Promise<SlaPolicy[]> {
    return http.get<SlaPolicy[]>('/sla-policies');
  },

  /** Users that can be attached as stakeholders when creating a ticket. */
  listStakeholderCandidates(params: { search?: string; clienteId?: UUID } = {}): Promise<Utente[]> {
    return http.get<Utente[]>(`${BASE}/stakeholder-candidates`, { params });
  },

  reopen(id: UUID): Promise<TicketConRelazioni> {
    return http.post<TicketConRelazioni>(`${BASE}/${id}/reopen`);
  },

  remove(id: UUID): Promise<void> {
    return http.delete(`${BASE}/${id}`);
  },

  cestino(filters: TicketFilters = {}): Promise<Paginated<TicketConRelazioni>> {
    return http.get<Paginated<TicketConRelazioni>>(`${BASE}/cestino`, { params: filters });
  },

  restore(id: UUID): Promise<TicketConRelazioni> {
    return http.post<TicketConRelazioni>(`${BASE}/${id}/ripristina`);
  },

  purge(id: UUID): Promise<void> {
    return http.delete(`${BASE}/${id}/definitivo`);
  },
};
